'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useMemo, useState, useSyncExternalStore } from 'react'
import { usePathname } from 'next/navigation'
import Container from '@/components/ui/Container'
import ButtonLink from '@/components/ui/ButtonLink'
import { FiMenu, FiX } from 'react-icons/fi'

const SCROLL_Y = 28

function subscribeScroll(cb: () => void) {
    window.addEventListener('scroll', cb, { passive: true })
    window.addEventListener('resize', cb, { passive: true })

    return () => {
        window.removeEventListener('scroll', cb)
        window.removeEventListener('resize', cb)
    }
}

function getScrollSnapshot() {
    return window.scrollY || 0
}

function getServerSnapshot() {
    return 0
}

const navigation = [
    { label: 'Lösungen', href: '#loesungen' },
    { label: 'Snowfox', href: '#snowfox' },
    { label: 'Elly', href: '#elly' },
    { label: 'Über uns', href: '#ueber-uns' },
    { label: 'Kontakt', href: '#kontakt' }
]

export default function Header() {
    const pathname = usePathname()
    const isHome = pathname === '/'
    const scrollY = useSyncExternalStore(subscribeScroll, getScrollSnapshot, getServerSnapshot)
    const [isOpen, setIsOpen] = useState(false)

    const scrolled = !isHome ? true : scrollY > SCROLL_Y

    const chrome = useMemo(() => {
        if (isHome && !scrolled) {
            return {
                outerClass: 'bg-transparent shadow-none',
                innerClass: 'py-6',
                navClass: 'text-text-on-dark/82',
                ctaVariant: 'light' as const,
                mobileButtonClass: 'bg-white/12 text-white backdrop-blur-md',
                logoSrc: '/logo/Light/Full/VELOG_RGB_Light_Full.svg',
                logoHeight: 'h-14 sm:h-16'
            }
        }

        return {
            outerClass: 'shadow-md',
            innerClass: 'py-4',
            navClass: 'text-text/78',
            ctaVariant: 'solid' as const,
            mobileButtonClass: 'bg-surface text-text',
            logoSrc: '/logo/Dark/Full/VELOG_RGB_Dark_Full.svg',
            logoHeight: 'h-11 sm:h-12'
        }
    }, [isHome, scrolled])

    return (
        <header
            aria-label='Website-Kopfbereich'
            className={[
                'fixed inset-x-0 top-0 z-50 transition-[background,box-shadow,backdrop-filter] duration-400',
                chrome.outerClass
            ].join(' ')}
            style={
                scrolled
                    ? ({
                          background: 'linear-gradient(180deg, rgba(255,255,255,0.88), rgba(255,255,255,0.84))',
                          backdropFilter: 'blur(18px)',
                          WebkitBackdropFilter: 'blur(18px)'
                      } as React.CSSProperties)
                    : undefined
            }>
            <Container>
                <div
                    className={[
                        'flex items-center justify-between gap-6 transition-[padding] duration-400',
                        chrome.innerClass
                    ].join(' ')}>
                    <Link href='/' aria-label='Zur Startseite von VELOG'>
                        <Image
                            src={chrome.logoSrc}
                            alt='VELOG GmbH'
                            width={520}
                            height={120}
                            priority
                            className={[
                                'w-auto drop-shadow-[0_14px_32px_rgba(0,0,0,0.22)] transition-[height] duration-400',
                                chrome.logoHeight
                            ].join(' ')}
                        />
                    </Link>

                    <nav className='hidden items-center gap-7 lg:flex'>
                        {navigation.map((item) => {
                            const href = isHome ? item.href : `/${item.href}`

                            return (
                                <Link
                                    key={item.label}
                                    href={href}
                                    className={[
                                        'text-sm font-semibold transition-colors duration-400 hover:text-brand',
                                        chrome.navClass
                                    ].join(' ')}>
                                    {item.label}
                                </Link>
                            )
                        })}
                    </nav>

                    <div className='hidden lg:flex'>
                        <ButtonLink href={isHome ? '#kontakt' : '/#kontakt'} variant={chrome.ctaVariant}>
                            Gespräch anfragen
                        </ButtonLink>
                    </div>

                    <button
                        type='button'
                        aria-expanded={isOpen}
                        aria-controls='mobile-navigation'
                        aria-label={isOpen ? 'Navigation schließen' : 'Navigation öffnen'}
                        className={[
                            'inline-flex h-12 w-12 items-center justify-center rounded-full shadow-sm lg:hidden',
                            chrome.mobileButtonClass
                        ].join(' ')}
                        onClick={() => setIsOpen((prev) => !prev)}>
                        {isOpen ? <FiX className='text-[24px]' /> : <FiMenu className='text-[24px]' />}
                    </button>
                </div>
            </Container>

            {isOpen ? (
                <div
                    id='mobile-navigation'
                    className='border-none bg-[rgba(10,18,35,0.82)] px-5 pb-5 pt-2 shadow-lg backdrop-blur-xl lg:hidden'>
                    <Container>
                        <nav className='rounded-xl bg-white/95 p-3 shadow-lg'>
                            <div className='flex flex-col'>
                                {navigation.map((item) => {
                                    const href = isHome ? item.href : `/${item.href}`

                                    return (
                                        <Link
                                            key={item.label}
                                            href={href}
                                            className='rounded-lg px-4 py-3 text-sm font-semibold text-text hover:bg-page-soft'
                                            onClick={() => setIsOpen(false)}>
                                            {item.label}
                                        </Link>
                                    )
                                })}
                            </div>

                            <div className='px-3 pb-3 pt-2'>
                                <ButtonLink
                                    href={isHome ? '#kontakt' : '/#kontakt'}
                                    variant='solid'
                                    className='w-full justify-center'>
                                    Gespräch anfragen
                                </ButtonLink>
                            </div>
                        </nav>
                    </Container>
                </div>
            ) : null}
        </header>
    )
}
